import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { orpc } from "@/client";
import { SystemSchemas } from "@/api/router/system/schemas";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

import { GuidanceFeedbackEntry } from "./guidance-feedback";

export function GuidanceProposalDialog({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const {
		data: feedback,
		isPending: feedbackPending,
		isError: feedbackError,
		isFetching: feedbackFetching,
		refetch: refetchFeedback,
	} = useQuery(orpc.system.guidanceFeedback.queryOptions());
	const form = useForm<typeof SystemSchemas.guidanceProposal.infer>({
		resolver: arktypeResolver(SystemSchemas.guidanceProposal),
		mode: "onChange",
		defaultValues: { content: "", feedback_event_ids: [] },
	});
	const selectedIds = useWatch({ control: form.control, name: "feedback_event_ids" });
	const selectedPeople = new Set(
		feedback?.filter((event) => selectedIds.includes(event.id)).map((event) => event.person_id),
	).size;
	const { mutate, isPending, data } = useMutation(
		orpc.system.proposeGuidance.mutationOptions({
			onSuccess: (result) => {
				if (result.ok) {
					queryClient.invalidateQueries({ queryKey: orpc.system.guidance.key() });
					toast.success("orientação proposta para revisão");
					onClose();
				}
			},
			onError: () => toast.error("não foi possível salvar a proposta"),
		}),
	);

	return (
		<Modal
			component="guidance-proposal-dialog"
			title="propor orientação"
			description="descreva uma orientação geral e selecione decisões de pelo menos duas pessoas diferentes que sustentam essa mudança."
			onClose={onClose}
			isPending={isPending}
			className="max-w-2xl"
		>
			<form onSubmit={form.handleSubmit((input) => mutate(input))} className="flex flex-col gap-4">
				<label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
					orientação para a triagem
					<Textarea
						{...form.register("content")}
						aria-label="orientação para a triagem"
						autoFocus
						rows={4}
						disabled={isPending}
					/>
				</label>
				<fieldset disabled={isPending} className="flex flex-col gap-2">
					<legend className="mb-2 text-sm font-medium text-foreground">
						decisões que sustentam a orientação
					</legend>
					{feedbackPending && <SkeletonRows rows={3} />}
					{feedbackError && (
						<div role="alert">
							<p className="text-sm text-destructive">não foi possível carregar as decisões.</p>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								disabled={feedbackFetching}
								onClick={() => refetchFeedback()}
							>
								<RefreshCw /> tentar de novo
							</Button>
						</div>
					)}
					{feedback?.length === 0 && (
						<p className="text-sm text-muted-foreground">
							as decisões registradas nas pendências aparecerão aqui.
						</p>
					)}
					<Controller
						control={form.control}
						name="feedback_event_ids"
						render={({ field }) => (
							<ul className="flex flex-col gap-2">
								{feedback?.map((event) => (
									<li
										key={event.id}
										className="flex items-start gap-3 rounded-lg border border-border p-3"
									>
										<input
											type="checkbox"
											aria-label={`selecionar decisão de ${event.name}: ${event.note}`}
											checked={field.value.includes(event.id)}
											onChange={(change) =>
												field.onChange(
													change.target.checked
														? [...field.value, event.id]
														: field.value.filter((id) => id !== event.id),
												)
											}
											className="mt-1 size-4 shrink-0 accent-primary"
										/>
										<GuidanceFeedbackEntry feedback={event} />
									</li>
								))}
							</ul>
						)}
					/>
				</fieldset>
				{selectedIds.length > 0 && selectedPeople < 2 && (
					<p className="text-sm text-muted-foreground">
						selecione também uma decisão de outra pessoa.
					</p>
				)}
				{data?.ok === false && (
					<p role="alert" className="text-sm text-destructive">
						a proposta precisa de decisões registradas em pelo menos duas pessoas diferentes.
					</p>
				)}
				<div className="flex justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={isPending}
						onClick={onClose}
					>
						cancelar
					</Button>
					<Button
						type="submit"
						size="sm"
						disabled={isPending || !form.formState.isValid || selectedPeople < 2}
					>
						{isPending && <Loader2 className="animate-spin" />} salvar proposta
					</Button>
				</div>
			</form>
		</Modal>
	);
}
